import { v4 as uuidv4 } from 'uuid';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { ValidationErrors } from 'final-form';

import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { store } from 'CorePackage/MainModule/common/store';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { KNM_SAVE_ERROR_ADDRESS_ERROR } from 'CorePackage/MainModule/constants/notices';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';

import {
  knmChangeStatus,
  knmCopy,
  knmDraftDelete,
  knmRemoveVisibleModal,
  knmSetOnSaveButtonClick,
  knmSetOpenRemoveFromPlanModal,
  knmSignSubmitFail,
  knmSignUploadModalApply,
} from '../actions/knmActions';
import { IKnmFormRef } from '../interfaces/IKnmFormRef';
import { KnmModalsEnum } from '../enums/KnmModalsEnum';
import { IKnoOrganisationChild } from '../../../APIPackage/CatalogsModule/interfaces/IKnoOrganisationChild';
import {
  IAccessSettingsForm,
  IAccessSettingsFormRef,
} from '../../../KnmPmPackage/MainModule/components/AccessSettingsModal';
import { userInfoSetHighlightedSelectedOrganisations } from '../../../PersonalAccountPackage/UserInfoModule/actions/userInfoActions';
import { KnmSystemTypesEnum } from '../enums/KnmSystemTypesEnum';
import { getKnmDataCreated, getKnmSystemType } from '../selectors/knmSelectors';

interface IUseKnmFormRenderProps {
  formRef: IKnmFormRef;
  isChangeStatusToApprove: boolean;
  knmSystemType: KnmSystemTypesEnum | undefined;
  setCanSubmit: (canSubmit: boolean) => void;
  accessSettingsFormRef: IAccessSettingsFormRef | undefined;
  handleClearDraftPassportData: () => void;
  knmData?: IKnm;
}

export function useKnmFormRender({
  formRef,
  isChangeStatusToApprove,
  accessSettingsFormRef,
  knmSystemType,
  handleClearDraftPassportData,
  knmData,
}: IUseKnmFormRenderProps) {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const knmSystemTypeRef = useRef(knmSystemType);

  knmSystemTypeRef.current = knmSystemType;

  const fetchKnmCopy: () => void = useCallback(() => dispatch(knmCopy()), [dispatch]);
  const setOnSaveButtonClick: (onSaveButtonClick: () => void) => void = useCallback(
    (onSaveButtonClick: () => void) => dispatch(knmSetOnSaveButtonClick(onSaveButtonClick)),
    [dispatch],
  );
  const setOpenRemoveFromPlanModal: (openRemoveFromPlanModal: () => void) => void = useCallback(
    (openRemoveFromPlanModal: () => void) => dispatch(knmSetOpenRemoveFromPlanModal(openRemoveFromPlanModal)),
    [dispatch],
  );
  const [isRemoveFromPlanVisible, setIsRemoveFromPlanVisible] = useState(false);
  const openRemoveFromPlanModal = useCallback(() => setIsRemoveFromPlanVisible(true), []);
  const closeRemoveFromPlanModal = () => setIsRemoveFromPlanVisible(false);

  useEffect(() => {
    setOpenRemoveFromPlanModal(openRemoveFromPlanModal);
  }, [openRemoveFromPlanModal, setOpenRemoveFromPlanModal]);

  const closeCopyModal = () => dispatch(knmRemoveVisibleModal(KnmModalsEnum.COPY_KNM_MODAL));

  const handleCopyModalApply = () => {
    fetchKnmCopy();
    closeCopyModal();
  };

  const closeChangeStatusModal = () => {
    dispatch(knmRemoveVisibleModal(KnmModalsEnum.CHANGE_STATUS_MODAL_TO_APPROVE));
    dispatch(knmRemoveVisibleModal(KnmModalsEnum.CHANGE_STATUS_MODAL_TO_REVISION));
  };
  const handleChangeStatusModalApply = (commentText?: string) => {
    if (isChangeStatusToApprove) {
      formRef.current.change('inspectionDecision', undefined);
      formRef.current.change('rejectReasons', undefined);
    }
    dispatch(knmChangeStatus(commentText));
  };

  const closeSignModal = () => dispatch(knmRemoveVisibleModal(KnmModalsEnum.SIGN_MODAL));

  const closeSignUploadModal = () => dispatch(knmRemoveVisibleModal(KnmModalsEnum.SIGN_UPLOAD_MODAL));

  const handleSignUploadModalApply = () => {
    dispatch(knmSignUploadModalApply());
  };

  /**
   * Функция валидации адресов в форме. Дефект #476572
   */
  const getFormAddressValidationError = useCallback(
    (errors: ValidationErrors) => {
      const state = store.getState();
      const isPm: boolean = getKnmSystemType(state) === KnmSystemTypesEnum.PM;

      const settings = getAdminFetchSettings(state);
      const created = getKnmDataCreated(state);
      const isUse4123 = isLaterThanAdminSetting(
        created,
        settings,
        AdminSettingCodesEnum.KNM_VALIDATION_4_1_23_STARTS_FROM,
      );

      let hasAddressValidationError = false;

      if (isUse4123 && errors) {
        if (isPm) {
          hasAddressValidationError =
            Array.isArray(errors.objectsPm) &&
            !!errors.objectsPm.find((i: { [key: string]: string | undefined }) => !!i?.address);

          if (!hasAddressValidationError) {
            hasAddressValidationError = !!errors.location;
          }
        } else {
          hasAddressValidationError =
            Array.isArray(errors.objectsErknm) &&
            !!errors.objectsErknm.find((i: { [key: string]: string | undefined }) => !!i?.address);

          if (!hasAddressValidationError) {
            hasAddressValidationError =
              Array.isArray(errors.places) &&
              !!errors.places.find((i: { [key: string]: string | undefined }) => !!i?.value);
          }

          if (!hasAddressValidationError) {
            hasAddressValidationError = !!errors.placeDecision;
          }
        }
      }

      return hasAddressValidationError;
    },
    [store],
  );

  const deleteAutosavedDraft = useCallback(() => {
    if (knmData) {
      dispatch(knmDraftDelete(knmData.id, handleClearDraftPassportData));
    }
  }, [knmData, handleClearDraftPassportData]);

  const deleteInitialInvalidFieldsPm = () => {
    sessionStorage.removeItem('initialValuesPm');
    sessionStorage.removeItem('initialInvalidFields');
  };

  const handleSaveButtonClick = useCallback(() => {
    formRef.current.change('shouldValidationForGeneratedDecision', false);
    formRef.current.change('shouldValidationForGeneratedWarning', false);
    const { errors: currentErrors, hasValidationErrors: isValidationErrors, values } = formRef.current.getState();

    const isDraft = values?.draft;

    formRef.current.submit();

    if (!isValidationErrors && knmData?.id && isDraft) {
      deleteAutosavedDraft();
    }

    if (!isValidationErrors && !knmData?.id) {
      deleteInitialInvalidFieldsPm();
    }

    const hasAddressValidationError = getFormAddressValidationError(currentErrors);

    if (hasAddressValidationError) {
      dispatch(
        addNoticeAction({
          renderContent: KNM_SAVE_ERROR_ADDRESS_ERROR,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.SAVE_KNM,
        }),
      );
    }

    if (isValidationErrors && !hasAddressValidationError) {
      dispatch(knmSignSubmitFail(currentErrors));
    }
  }, [dispatch, formRef, knmSystemTypeRef, knmData]);

  const handleCloseAccessSettingsModal = () => dispatch(knmRemoveVisibleModal(KnmModalsEnum.ACCESS_SETTINGS));

  const handleCloseOrganisationsModal = () =>
    dispatch(knmRemoveVisibleModal(KnmModalsEnum.DICTIONARY_KNO_ORGANISATIONS));

  const handleCloseErknmCopyModal = () => dispatch(knmRemoveVisibleModal(KnmModalsEnum.ERKNM_COPY_MODAL));

  const handleCloseErknmPrescribeCancelModal = () =>
    dispatch(knmRemoveVisibleModal(KnmModalsEnum.ERKNM_PRESCRIBE_CANCEL));

  const handleCloseErknmActCancelModal = () => dispatch(knmRemoveVisibleModal(KnmModalsEnum.ERKNM_ACT_CANCEL));

  const handleCloseSignAfterCancelModal = () => dispatch(knmRemoveVisibleModal(KnmModalsEnum.ERKNM_SIGN_AFTER_CANCEL));

  const handleCloseErknmFinishRoPassportModal = () =>
    dispatch(knmRemoveVisibleModal(KnmModalsEnum.ERKNM_FINISH_RO_PASSPORT));

  const handleClosePmOpCloseWarningModal = () =>
    dispatch(knmRemoveVisibleModal(KnmModalsEnum.PM_OP_CANCEL_WARNING_PASSPORT));

  const handleApplyOrganisationsModal = useCallback(
    (organisations: IKnoOrganisationChild[]) => {
      const currentOrganisations = accessSettingsFormRef?.current.getFieldState(
        'accessPrivilege' as keyof IAccessSettingsForm,
      )?.value;
      const duplicates = organisations.filter(item =>
        currentOrganisations?.some(currentItem => currentItem.organisation?.dictVersionId === item.dictVersionId),
      );

      if (duplicates?.length) {
        dispatch(userInfoSetHighlightedSelectedOrganisations(duplicates));

        return;
      }

      const accessSettingsPush = accessSettingsFormRef?.current.mutators.push;

      accessSettingsPush &&
        organisations.forEach(organisation =>
          accessSettingsPush('accessPrivilege' as keyof IAccessSettingsFormRef, { organisation, guid: uuidv4() }),
        );
      handleCloseOrganisationsModal();
    },
    [accessSettingsFormRef],
  );

  const handleCloseErknmLinkOtherWarningModal = () =>
    dispatch(knmRemoveVisibleModal(KnmModalsEnum.ERKNM_LINK_OTHER_WARNING_MODAL));

  useEffect(() => {
    setOnSaveButtonClick(handleSaveButtonClick);
  }, [handleSaveButtonClick, setOnSaveButtonClick]);

  return {
    isRemoveFromPlanVisible,
    closeRemoveFromPlanModal,
    handleCopyModalApply,
    closeCopyModal,
    handleChangeStatusModalApply,
    closeChangeStatusModal,
    closeSignModal,
    closeSignUploadModal,
    handleSignUploadModalApply,
    handleCloseAccessSettingsModal,
    handleCloseOrganisationsModal,
    handleApplyOrganisationsModal,
    handleCloseErknmCopyModal,
    handleCloseErknmPrescribeCancelModal,
    handleCloseErknmActCancelModal,
    handleCloseErknmFinishRoPassportModal,
    handleClosePmOpCloseWarningModal,
    handleCloseSignAfterCancelModal,
    handleCloseErknmLinkOtherWarningModal,
  };
}
